import { signal, effect, startBatch, endBatch } from "alien-signals";
import { Tracker } from "../models/Tracker.js";
import { uiTheme } from "./ui-theme-signals.js";
import { defaultPdfAssets } from "./pdf-assets.js";

export const loggedIn = signal(false);
export const libHandler = signal();
export const tosLink = signal("");
export const locale = signal("en");
export const startPath = signal("");
export const pageNumber = signal(1);
export const tracker = signal();
export const lastReadingPosition = signal();
export const embedded = signal(false);
export const mode = signal();
export const bookplateFilenames = signal();
export const jbksBookplate = signal();
export const manifestConfig = signal();
export const assetsUrl = signal();
export const element = signal();
export const manifestExpiry = signal();
export const bookmarksHandler = signal();
export const lastPositionRoute = signal();
export const rendition = signal();
export const pdfWorkerPath = signal(defaultPdfAssets.workerSrc);
export const pdfcmaps = signal(defaultPdfAssets.cMapUrl);
export const showNavigation = signal(true);
export const autoHideToolbar = signal(3500);
export const disableAnimation = signal(false);

document.documentElement.addEventListener("cassis:hideToolbar", function () {
  console.log("hiding navigation");
  showNavigation(false);
});

effect(() => {
  const className = showNavigation() ? "cassis-show-navigation" : "";
  const el = element();
  if (el && className) {
    el.classList.add(className);
  }
});

export function setupAppSignals(
  config,
  {
    url,
    // Rails passes both (pdf-worker-src, pdf-cmaps-src); without them a
    // build uses the copies it was built with.
    workerPath = defaultPdfAssets.workerSrc,
    cmaps = defaultPdfAssets.cMapUrl,
  } = {},
) {
  // One batch, so that the document is requested once, with the final
  // worker and cmaps locations, rather than once per signal written.
  startBatch();
  try {
    applyConfig(config, { url, workerPath, cmaps });
  } finally {
    endBatch();
  }
}

function applyConfig(config, { url, workerPath, cmaps }) {
  const configWithDefaults = Object.assign(
    {
      startPage: 1,
      locale: "en",
      autoHideToolbar: 3500,
      disableAnimation: false,
    },
    config,
  );
  autoHideToolbar(configWithDefaults.autoHideToolbar);
  disableAnimation(configWithDefaults.disableAnimation);
  // Default to night
  uiTheme("night");
  pdfWorkerPath(workerPath);
  pdfcmaps(cmaps);
  // startPath is the PDF path. The PDF config's url wins: an embedder
  // passing its EPUB options through may set startPath to something
  // that is not the PDF.
  startPath(url || configWithDefaults.startPath);
  // startpage is the pdf starting page number
  pageNumber(configWithDefaults.startPage || 1);
  loggedIn(
    (configWithDefaults.loggedIn && !configWithDefaults.embedded) || false,
  );
  libHandler(
    window.location === window.parent.location
      ? configWithDefaults.libHandler
      : undefined,
  );
  locale(configWithDefaults.locale || "en");
  tosLink(configWithDefaults.tosLink);
  lastReadingPosition(configWithDefaults.lastReadingPosition);
  embedded(configWithDefaults.embedded);
  // mode(configWithDefaults.mode);
  // bookplateFilenames(configWithDefaults.bookplateFilenames);
  // jbksBookplate(configWithDefaults.jbksBookplate);
  // assetsUrl(configWithDefaults.assetsUrl);
  // manifestExpiry(configWithDefaults.manifestExpiry);
  // For PDFs rendition will be "PDF"
  // rendition(configWithDefaults.rendition);
  if (configWithDefaults.annotationEndpoints) {
    // Each of these is a URL
    bookmarksHandler(configWithDefaults.annotationEndpoints.bookmarks);
    lastPositionRoute(
      configWithDefaults.annotationEndpoints.lastReadingPosition,
    );
  }
  element(configWithDefaults.element);

  // const fallbackLocation = window.location.href.endsWith("/")
  // 	? window.location.href + "streamer/"
  // 	: window.location.href + "/streamer/";
  // manifestConfig({
  // 	rawManifest: configWithDefaults.rawManifest || null,
  // 	// We’ve been serving manifest from the same URL as window.location
  // 	// So in case there is a rawManifest Object, it can serve as a virtual URL
  // 	// that provides backwards compatibility
  // 	manifestUrl:
  // 		configWithDefaults.manifestUrl || new URL("manifest.json", fallbackLocation),
  // });
  // Bookstore tied to annotations
  if (configWithDefaults.tracker) {
    tracker(
      new Tracker({
        post_url: config.tracker.post_url, // "https://staging.jellybooks.com/crowberry_post",
        epub_id: config.tracker.epub_id, // "",
        heartbeat_rate: config.tracker.heartbeat_rate, // 10,
        reader_type: config.tracker.reader_type, // "cassis-sample",
        version: config.tracker.version, //"1.0"
        buyButton: config.tracker.buyButton, // 'jb-buy'
      }),
    );
  }
}

// Effect that renders to element if signal is set.
effect(() => {
  const el = element();
  if (el) {
    el.innerHTML = `<cassis-reader></cassis-reader>`;
  }
});
